/**
 * Customer Store Public API
 */

import { customerStoreActions } from './customer-store.actions';
import type { CustomerAddress, Customer, Maybe } from '../types/front.graphql';

// ============================================================================
// STATE GETTERS - READ-ONLY ACCESS
// ============================================================================

export const customerStoreStateAPI = {
    /** Get currentCustomer - matches mobile's customerProfile$.currentCustomer */
    getCurrentCustomer: (): Customer | undefined => customerStoreActions.getCurrentCustomer(),

    getAddresses: (): readonly CustomerAddress[] => customerStoreActions.getAddresses(),

    getDefaultShippingAddressId: (): Maybe<number> | undefined => customerStoreActions.getDefaultShippingAddressId(),

    getDefaultBillingAddressId: (): Maybe<number> | undefined => customerStoreActions.getDefaultBillingAddressId(),

    getSelectedAddressId: (): Maybe<number> | undefined => customerStoreActions.getSelectedAddressId(),

    /** Get defaultOrFirstAddress - matches mobile's customerProfile$.defaultOrFirstAddress */
    getDefaultOrFirstAddress: (): CustomerAddress | undefined => customerStoreActions.getDefaultOrFirstAddress(),

    getIsLoading: (): boolean => customerStoreActions.getIsLoading(),

    getError: (): string | undefined => customerStoreActions.getError(),

    getState: () => {
        const state = customerStoreActions.getState();
        return Object.freeze({
            ...state,
            addresses: Object.freeze(state.addresses),
        });
    },
} as const;

// ============================================================================
// COMPUTED VALUES - READ-ONLY ACCESS
// ============================================================================

export const customerStoreComputedAPI = {
    hasAddresses: (): boolean => customerStoreActions.getAddresses().length > 0,

    getAddressCount: (): number => customerStoreActions.getAddresses().length,

    getAddressById: (addressId: Maybe<number>): CustomerAddress | undefined =>
        customerStoreActions.getAddresses().find(a => a.id === addressId),

    getDefaultShippingAddress: (): CustomerAddress | undefined => {
        const id = customerStoreActions.getDefaultShippingAddressId();
        if (!id) return undefined;
        return customerStoreActions.getAddresses().find(a => a.id === id);
    },

    getDefaultBillingAddress: (): CustomerAddress | undefined => {
        const id = customerStoreActions.getDefaultBillingAddressId();
        if (!id) return undefined;
        return customerStoreActions.getAddresses().find(a => a.id === id);
    },

    getSelectedAddress: (): CustomerAddress | undefined => {
        const id = customerStoreActions.getSelectedAddressId();
        if (!id) return undefined;
        return customerStoreActions.getAddresses().find(a => a.id === id);
    },
} as const;

// ============================================================================
// MUTATIONS - CONTROLLED ACCESS
// ============================================================================

export const customerStoreMutationAPI = {
    /** Set currentCustomer - matches mobile's customerProfile$.currentCustomer.set() */
    setCurrentCustomer: (customer: Customer | undefined): void => customerStoreActions.setCurrentCustomer(customer),

    setAddresses: (addresses: CustomerAddress[]): void => customerStoreActions.setAddresses(addresses),

    addAddress: (address: CustomerAddress): void => customerStoreActions.addAddress(address),

    updateAddress: (address: CustomerAddress): void => customerStoreActions.updateAddress(address),

    removeAddress: (addressId: Maybe<number>): void => customerStoreActions.removeAddress(addressId),

    setSelectedAddressId: (addressId: Maybe<number> | undefined): void =>
        customerStoreActions.setSelectedAddressId(addressId),

    /** Set defaultOrFirstAddress - matches mobile's customerProfile$.defaultOrFirstAddress.set() */
    setDefaultOrFirstAddress: (address: CustomerAddress | undefined): void =>
        customerStoreActions.setDefaultOrFirstAddress(address),

    setLoading: (isLoading: boolean): void => customerStoreActions.setLoading(isLoading),

    setError: (error: string | undefined): void => customerStoreActions.setError(error),

    resetStore: (): void => customerStoreActions.resetStore(),
} as const;

// ============================================================================
// UNIFIED PUBLIC API
// ============================================================================

export const customerStoreAPI = {
    state: customerStoreStateAPI,
    computed: customerStoreComputedAPI,
    actions: customerStoreMutationAPI,
} as const;

export type CustomerStoreStateAPI = typeof customerStoreStateAPI;
export type CustomerStoreComputedAPI = typeof customerStoreComputedAPI;
export type CustomerStoreMutationAPI = typeof customerStoreMutationAPI;
export type CustomerStoreAPI = typeof customerStoreAPI;
